/**
 * Diagnostic Décret Tertiaire — funnel 4 étapes branché sur l'API
 * `/api/decret-tertiaire/diagnostic`.
 *
 * Calcule la trajectoire OPERAT du site (-40 % 2030, -50 % 2040, -60 % 2050)
 * à partir d'une consommation de référence en kWh ep/m²/an et renvoie
 * la cible chiffrée + une liste de recommandations + un CTA contact.
 *
 * Sources réglementaires : Décret n°2019-771 du 23 juillet 2019,
 * arrêté du 10 avril 2020 (« arrêté méthode »), barème ADEME 2025.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getAudienceById, type AudienceId } from '@shared/constants/audiences';
import {
  AlertTriangle,
  ArrowRight,
  Building,
  Building2,
  CheckCircle2,
  ChevronLeft,
  GraduationCap,
  HelpCircle,
  Hotel,
  ListChecks,
  Loader2,
  ShoppingBag,
  Sparkles,
  Stethoscope,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { FunnelStepper } from '../ui/funnel-stepper';
import { TrajectoryChart } from './TrajectoryChart';
import { apiFetch, ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';
import { captureEvent } from '../../lib/analytics';

/* ───── Types ─────────────────────────────────────────────────────────── */
type BuildingType = 'bureau' | 'commerce' | 'enseignement' | 'sante' | 'hotellerie';

interface FormState {
  buildingType: BuildingType | null;
  surfaceSqm: number;
  consumptionKwhM2Year: number;
  yearOfReference: number;
}

interface ApiResponse {
  eligible: boolean;
  message?: string;
  buildingType?: string;
  currentConsumption?: number;
  objectives?: {
    '2030': { reductionPct: number; targetKwhM2: number };
    '2040': { reductionPct: number };
    '2050': { reductionPct: number };
  };
  operatRequirements?: Record<string, unknown>;
  recommandations?: string[];
}

/* ───── Données saisie ────────────────────────────────────────────────── */
const BUILDING_TYPES: Array<{
  id: BuildingType;
  label: string;
  sublabel: string;
  icon: LucideIcon;
  defaultRefKwh: number;
}> = [
  {
    id: 'bureau',
    label: 'Bureaux',
    sublabel: 'Activités tertiaires administratives',
    icon: Building2,
    defaultRefKwh: 240,
  },
  {
    id: 'commerce',
    label: 'Commerce',
    sublabel: 'Magasin, boutique, supermarché',
    icon: ShoppingBag,
    defaultRefKwh: 410,
  },
  {
    id: 'enseignement',
    label: 'Enseignement',
    sublabel: 'École, collège, lycée, université',
    icon: GraduationCap,
    defaultRefKwh: 165,
  },
  {
    id: 'sante',
    label: 'Santé',
    sublabel: 'Hôpital, clinique, EHPAD',
    icon: Stethoscope,
    defaultRefKwh: 450,
  },
  {
    id: 'hotellerie',
    label: 'Hôtellerie / Restauration',
    sublabel: 'Hôtel, restaurant, hébergement',
    icon: Hotel,
    defaultRefKwh: 380,
  },
];

const STEP_LABELS = ['Bâtiment', 'Surface', 'Consommation', 'Résultats'];
const STEPPER_STEPS = STEP_LABELS.map((label, i) => ({ id: String(i), label }));

/* ───── Helpers format ────────────────────────────────────────────────── */
function fmt(n: number) {
  return Math.round(n).toLocaleString('fr-FR');
}

/* ───── Root ──────────────────────────────────────────────────────────── */
interface DecretTertiaireDiagnosticProps {
  /**
   * Verrouille le profil sur cette audience (entreprises / collectivites / coproprietes).
   * Affiche un badge contextuel indiquant le parcours sectoriel.
   */
  lockedAudience?: AudienceId;
}

export function DecretTertiaireDiagnostic({ lockedAudience }: DecretTertiaireDiagnosticProps = {}) {
  const lockedAudienceMeta = lockedAudience ? getAudienceById(lockedAudience) : null;
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState<FormState>({
    buildingType: null,
    surfaceSqm: 1500,
    consumptionKwhM2Year: 240,
    yearOfReference: 2010,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tracking PostHog : un event par changement d'étape.
  const prevStep = useRef(step);
  useEffect(() => {
    if (step !== prevStep.current) {
      prevStep.current = step;
      const labels = ['Bâtiment', 'Surface & année', 'Consommation', 'Résultats'];
      captureEvent('decret_step_view', {
        step,
        step_label: labels[step] ?? `Étape ${step + 1}`,
        tool: 'decret-tertiaire',
      });
      // Scroll to top du funnel (mobile : l'étape suivante serait hors écran)
      // + focus du contenu après la transition (a11y clavier/SR).
      document
        .getElementById('decret-funnel-top')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        document.getElementById('decret-step-content')?.focus({ preventScroll: true });
      }, 300);
    }
  }, [step]);

  function goNext() {
    setDir(1);
    setStep((s) => s + 1);
  }
  function goBack() {
    setDir(-1);
    setStep((s) => s - 1);
  }
  function reset() {
    setDir(-1);
    setForm({
      buildingType: null,
      surfaceSqm: 1500,
      consumptionKwhM2Year: 240,
      yearOfReference: 2010,
    });
    setResult(null);
    setError(null);
    setStep(0);
  }

  async function submit() {
    if (!form.buildingType) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<ApiResponse>('/api/decret-tertiaire/diagnostic', {
        json: {
          buildingType: form.buildingType,
          surfaceSqm: form.surfaceSqm,
          consumptionKwhM2Year: form.consumptionKwhM2Year,
          yearOfReference: form.yearOfReference,
        },
      });
      setResult(data);
      setDir(1);
      setStep(3);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : 'Impossible de calculer le diagnostic. Réessayez ou contactez-nous.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      id="decret-funnel-top"
      className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-elevated"
    >
      {/* Progress */}
      <div className="border-b border-stone-100 bg-stone-50/60 px-5 py-4 md:px-8">
        {lockedAudienceMeta && (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-stone-100 px-2 py-1 text-[11px] font-semibold text-stone-600">
            <span>Parcours {lockedAudienceMeta.shortLabel}</span>
          </div>
        )}
        <FunnelStepper steps={STEPPER_STEPS} current={step} className="w-full" />
      </div>

      {/* Content */}
      <div className="p-5 md:p-8">
        {step > 0 && step < 3 && (
          <button
            type="button"
            onClick={goBack}
            className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Retour
          </button>
        )}

        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            id="decret-step-content"
            tabIndex={-1}
            className="outline-none"
            custom={dir}
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.28, ease: easeStripe }}
          >
            {step === 0 && (
              <StepBuilding
                value={form.buildingType}
                onSelect={(v) => {
                  const meta = BUILDING_TYPES.find((b) => b.id === v);
                  setForm((f) => ({
                    ...f,
                    buildingType: v,
                    consumptionKwhM2Year: meta?.defaultRefKwh ?? f.consumptionKwhM2Year,
                  }));
                  goNext();
                }}
              />
            )}
            {step === 1 && (
              <StepSurface
                surface={form.surfaceSqm}
                year={form.yearOfReference}
                onChange={(surface, year) =>
                  setForm((f) => ({ ...f, surfaceSqm: surface, yearOfReference: year }))
                }
                onNext={goNext}
              />
            )}
            {step === 2 && (
              <StepConsumption
                value={form.consumptionKwhM2Year}
                buildingType={form.buildingType}
                surfaceSqm={form.surfaceSqm}
                onChange={(v) => setForm((f) => ({ ...f, consumptionKwhM2Year: v }))}
                onSubmit={submit}
                loading={loading}
                error={error}
              />
            )}
            {step === 3 && result && <StepResults result={result} form={form} onReset={reset} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ───── Step 0 — Type de bâtiment ─────────────────────────────────────── */
function StepBuilding({
  value,
  onSelect,
}: {
  value: BuildingType | null;
  onSelect: (v: BuildingType) => void;
}) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-400">
        Étape 1 / 3
      </p>
      <h3 className="h-section mt-2">Quel type de bâtiment tertiaire ?</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Le décret n° 2019-771 s’applique aux bâtiments à usage tertiaire de plus de 1 000 m².
      </p>

      <fieldset className="m-0 mt-6 border-0 p-0">
        <legend className="sr-only">Quel type de bâtiment tertiaire ?</legend>
        <div
          role="radiogroup"
          aria-label="Type de bâtiment tertiaire"
          className="grid gap-2.5 sm:grid-cols-2"
        >
          {BUILDING_TYPES.map((b) => {
            const Icon = b.icon;
            const isSelected = value === b.id;
            return (
              <button
                key={b.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onSelect(b.id)}
                className={cn(
                  'group flex w-full items-center gap-3.5 rounded-xl border-2 bg-white p-4 text-left transition-all duration-150 ease-out hover:shadow-[0_4px_16px_rgba(139,92,246,0.07)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-400 focus-visible:ring-offset-2',
                  isSelected
                    ? 'border-lavender-400 bg-lavender-50/60'
                    : 'border-stone-200 hover:border-stone-300',
                )}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lavender-50 text-lavender-700">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-ink">{b.label}</p>
                  <p className="mt-0.5 text-[12px] text-stone-500">{b.sublabel}</p>
                </div>
                <ArrowRight
                  className={cn(
                    'h-4 w-4 shrink-0 transition-all duration-150',
                    isSelected
                      ? 'translate-x-0.5 text-lavender-500'
                      : 'text-stone-300 group-hover:translate-x-0.5 group-hover:text-lavender-500',
                  )}
                />
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-stone-50 p-3">
        <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden />
        <p className="text-[11px] text-stone-500">
          Logistique, industrie, agriculture et résidentiel ne sont pas concernés par le décret
          tertiaire (cf. arrêté méthode 10 avril 2020).
        </p>
      </div>
    </div>
  );
}

/* ───── Step 1 — Surface + année de référence ─────────────────────────── */
function StepSurface({
  surface,
  year,
  onChange,
  onNext,
}: {
  surface: number;
  year: number;
  onChange: (surface: number, year: number) => void;
  onNext: () => void;
}) {
  const tooSmall = surface > 0 && surface < 1000;
  const yearInvalid = year > 0 && (year < 2010 || year > 2019);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!tooSmall && !yearInvalid && surface >= 1000) onNext();
      }}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-400">
        Étape 2 / 3
      </p>
      <h3 className="h-section mt-2">Surface assujettie et année de référence</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        L’année de référence est celle utilisée pour calculer l’objectif en valeur relative (méthode
        Crelat). Doit se situer entre 2010 et 2019.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {/* Surface */}
        <div>
          <label htmlFor="dt-surface" className="text-[12px] font-semibold text-stone-700">
            Surface tertiaire (m²)
          </label>
          <div className="mt-1.5 flex rounded-xl border-2 border-stone-200 bg-white transition-colors focus-within:border-lavender-400">
            <input
              id="dt-surface"
              type="number"
              inputMode="numeric"
              min={0}
              step={100}
              value={surface || ''}
              onChange={(e) => onChange(Number(e.target.value) || 0, year)}
              aria-describedby="dt-surface-hint"
              className="w-full bg-transparent px-4 py-3 text-[15px] font-semibold text-ink outline-none placeholder:text-stone-400"
              placeholder="1500"
            />
            <span
              className="flex items-center px-4 text-[12px] font-medium text-stone-400"
              aria-hidden
            >
              m²
            </span>
          </div>
          <p id="dt-surface-hint" className="mt-1 text-[11px] text-stone-400">
            Seuil minimum : 1 000 m²
          </p>
        </div>

        {/* Année */}
        <div>
          <label htmlFor="dt-year" className="text-[12px] font-semibold text-stone-700">
            Année de référence
          </label>
          <div
            className={`mt-1.5 flex rounded-xl border-2 bg-white transition-colors ${yearInvalid ? 'border-red-400' : 'border-stone-200 focus-within:border-lavender-400'}`}
          >
            <input
              id="dt-year"
              type="number"
              inputMode="numeric"
              min={2010}
              max={2019}
              step={1}
              value={year || ''}
              onChange={(e) => onChange(surface, Number(e.target.value) || 2010)}
              aria-describedby="dt-year-hint"
              aria-invalid={yearInvalid || undefined}
              className="w-full bg-transparent px-4 py-3 text-[15px] font-semibold text-ink outline-none placeholder:text-stone-400"
              placeholder="2010"
            />
          </div>
          <p
            id="dt-year-hint"
            role={yearInvalid ? 'alert' : undefined}
            className={`mt-1 text-[11px] ${yearInvalid ? 'font-semibold text-red-600' : 'text-stone-400'}`}
          >
            {yearInvalid ? "L'année doit être comprise entre 2010 et 2019." : 'Entre 2010 et 2019'}
          </p>
        </div>
      </div>

      {tooSmall && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-[12px] font-medium text-amber-900">
            Avec moins de 1 000 m², votre bâtiment n’est pas assujetti au décret tertiaire. Vous
            pouvez tout de même bénéficier d’un audit volontaire via les CEE.
          </p>
        </motion.div>
      )}

      <Button
        variant="brand"
        size="xl"
        disabled={surface < 1000 || yearInvalid || year < 2010}
        onClick={onNext}
        className="mt-6 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>
    </form>
  );
}

/* ───── Step 2 — Consommation ─────────────────────────────────────────── */
function StepConsumption({
  value,
  buildingType,
  surfaceSqm,
  onChange,
  onSubmit,
  loading,
  error,
}: {
  value: number;
  buildingType: BuildingType | null;
  surfaceSqm: number;
  onChange: (v: number) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
}) {
  const buildingMeta = BUILDING_TYPES.find((b) => b.id === buildingType);
  const totalKwhYear = value * surfaceSqm;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (value > 0 && !loading) onSubmit();
      }}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-400">
        Étape 3 / 3
      </p>
      <h3 className="h-section mt-2">Consommation de référence</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        En kWh d’énergie primaire par m² par an, corrigée des variations climatiques. Disponible sur
        vos factures gaz/élec ou via OPERAT après déclaration.
      </p>

      <div className="mt-6">
        <label htmlFor="dt-consumption" className="text-[12px] font-semibold text-stone-700">
          Consommation (kWh ep / m² / an)
        </label>
        <div className="mt-1.5 flex rounded-xl border-2 border-stone-200 bg-white transition-colors focus-within:border-lavender-400">
          <input
            id="dt-consumption"
            type="number"
            inputMode="numeric"
            min={0}
            step={10}
            value={value || ''}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
            className="w-full bg-transparent px-4 py-3 text-[15px] font-semibold text-ink outline-none placeholder:text-stone-400"
            placeholder={String(buildingMeta?.defaultRefKwh ?? 240)}
          />
          <span className="flex items-center px-4 text-[12px] font-medium text-stone-400">
            kWh ep/m²/an
          </span>
        </div>

        {buildingMeta && (
          <p className="mt-1.5 text-[11px] text-stone-500">
            Référence moyenne pour ce type de bâtiment :{' '}
            <strong className="text-stone-700">{buildingMeta.defaultRefKwh} kWh ep/m²/an</strong>{' '}
            (ADEME).
          </p>
        )}
      </div>

      {/* Aperçu chiffré */}
      <div className="mt-5 rounded-xl border border-lavender-100 bg-lavender-50/50 p-4">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-lavender-700">
          Aperçu chiffré
        </p>
        <p className="mt-1 text-[13px] text-stone-700">
          Consommation totale annuelle estimée :{' '}
          <strong className="font-bold text-ink">{fmt(totalKwhYear)} kWh ep/an</strong>
          {value > 0 && surfaceSqm > 0 && (
            <>
              {' · '}
              soit <strong className="font-bold text-ink">{fmt(totalKwhYear * 0.085)} €</strong> de
              facture annuelle (0,085 €/kWh moyen)
            </>
          )}
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <p className="text-[12px] font-medium text-red-900">{error}</p>
        </motion.div>
      )}

      <Button
        type="submit"
        variant="brand"
        size="xl"
        disabled={value <= 0 || loading}
        className="mt-6 w-full gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Calcul en cours…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Calculer ma trajectoire OPERAT
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>
    </form>
  );
}

/* ───── Step 3 — Résultats ────────────────────────────────────────────── */
function StepResults({
  result,
  form,
  onReset,
}: {
  result: ApiResponse;
  form: FormState;
  onReset: () => void;
}) {
  if (!result.eligible || !result.objectives) {
    return (
      <div>
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50">
            <AlertTriangle className="h-6 w-6 text-amber-600" strokeWidth={1.7} />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700">
              Bâtiment non assujetti
            </p>
            <h3 className="h-section mt-1">Décret tertiaire non applicable</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-stone-600">
              {result.message ?? 'Votre bâtiment ne franchit pas le seuil de 1 000 m².'}
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50 p-4 text-[12px] text-stone-600">
          Vous pouvez tout de même bénéficier d’un audit volontaire ou d’aides via les Certificats
          d’Économies d’Énergie (CEE). Notre équipe peut chiffrer les primes.
        </div>

        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
          <Button asChild variant="brand" size="xl" className="flex-1 gap-2">
            <a href="/entreprises/diagnostic-cee">
              <Sparkles className="h-4 w-4" />
              Estimer mes primes CEE
            </a>
          </Button>
          <Button asChild variant="secondary" size="xl" className="flex-1">
            <a href="/contact">Parler à un expert</a>
          </Button>
        </div>

        <button
          onClick={onReset}
          className="mt-4 w-full text-center text-[12px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline"
        >
          Recommencer le diagnostic
        </button>
      </div>
    );
  }

  const obj2030 = result.objectives['2030'];
  const obj2040 = result.objectives['2040'];
  const obj2050 = result.objectives['2050'];
  const consumption = result.currentConsumption ?? form.consumptionKwhM2Year;
  const reductionKwh = consumption - obj2030.targetKwhM2;
  const reductionPct = Math.abs(obj2030.reductionPct);
  const economieEuros = Math.round(reductionKwh * form.surfaceSqm * 0.085);
  const recos = result.recommandations ?? [];

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: easeStripe }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-lavender-700">
              Objectif 2030
            </p>
            <p className="mt-1.5 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
              {fmt(obj2030.targetKwhM2)}
              <span className="text-2xl text-stone-400"> kWh ep/m²/an</span>
            </p>
            <p className="mt-1 text-[12px] text-stone-500">
              Soit <strong className="font-bold text-lavender-700">−{reductionPct} %</strong> vs.
              votre référence ({fmt(consumption)} kWh ep/m²/an)
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-lavender-50">
            <CheckCircle2 className="h-6 w-6 text-lavender-600" strokeWidth={1.7} />
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <KpiTile
            icon={TrendingDown}
            label="Réduction nécessaire"
            value={`${fmt(reductionKwh)} kWh/m²`}
            tone="lavender"
          />
          <KpiTile
            icon={Building}
            label="Surface assujettie"
            value={`${fmt(form.surfaceSqm)} m²`}
            tone="brand"
          />
          <KpiTile
            icon={Sparkles}
            label="Économie potentielle"
            value={`~${fmt(economieEuros).replace(/\s/g, ' ')} €/an`}
            sublabel="Hypothèse : 0,085 €/kWh (tarif B2B moyen)"
            tone="accent"
          />
        </div>

        {/* Trajectoire */}
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">
            Trajectoire OPERAT
          </p>
          <h4 className="mt-1 font-display text-[15px] font-semibold text-ink">
            Votre courbe 2020 → 2050
          </h4>
          <div className="mt-3">
            <TrajectoryChart referenceConsumption={consumption} />
          </div>
        </div>

        {/* Échéances */}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <ObjectiveTile
            year={2030}
            reductionPct={Math.abs(obj2030.reductionPct)}
            kwhM2={obj2030.targetKwhM2}
            highlight
          />
          <ObjectiveTile
            year={2040}
            reductionPct={Math.abs(obj2040.reductionPct)}
            kwhM2={consumption * (1 + obj2040.reductionPct / 100)}
          />
          <ObjectiveTile
            year={2050}
            reductionPct={Math.abs(obj2050.reductionPct)}
            kwhM2={consumption * (1 + obj2050.reductionPct / 100)}
          />
        </div>

        {/* Recommandations */}
        {recos.length > 0 && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50/60 p-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-lavender-600" strokeWidth={1.7} />
              <p className="text-[12px] font-bold uppercase tracking-widest text-stone-700">
                Plan d’actions recommandé
              </p>
            </div>
            <ul className="mt-3 space-y-2">
              {recos.map((r) => (
                <li key={r} className="flex items-start gap-2 text-[13px] text-stone-700">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lavender-500" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warning */}
        <div className="mt-5 flex items-start gap-2 rounded-xl border border-stone-200 bg-stone-50 p-3">
          <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
          <p className="text-[11px] leading-relaxed text-stone-500">
            Estimation indicative basée sur la méthode Crelat (valeur relative) de l’arrêté du 10
            avril 2020. Pour l’objectif final, OPERAT vérifie aussi la cible Cabs (valeur absolue)
            selon votre catégorie et sous-catégorie. Notre audit complet documente les deux
            méthodes.
          </p>
        </div>

        {/* CTA + capture lead inline */}
        <DecretLeadCapture form={form} result={result} />

        <button
          onClick={onReset}
          className="mt-4 w-full text-center text-[12px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline"
        >
          Recommencer le diagnostic
        </button>
      </motion.div>
    </div>
  );
}

function DecretLeadCapture({ form, result }: { form: FormState; result: ApiResponse }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (done) {
    return (
      <div className="mt-6 rounded-xl border border-brand-200 bg-brand-50/50 p-4 text-center">
        <CheckCircle2 className="mx-auto h-6 w-6 text-brand-700" strokeWidth={2} />
        <p className="mt-2 text-[14px] font-semibold text-ink">Demande envoyée</p>
        <p className="mt-1 text-[12.5px] text-stone-600">
          Un expert OPERAT vous recontacte sous 24 h ouvrées avec votre plan d&apos;actions.
        </p>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErr('Adresse e-mail invalide');
      return;
    }
    setErr(null);
    setLoading(true);
    try {
      const obj2030 = result.objectives?.['2030'];
      const message = [
        `Diagnostic Décret Tertiaire`,
        `Bâtiment: ${form.buildingType ?? '-'}`,
        `Surface: ${form.surfaceSqm} m²`,
        `Conso référence: ${form.consumptionKwhM2Year} kWh/m²/an (${form.yearOfReference})`,
        obj2030
          ? `Cible 2030: ${obj2030.targetKwhM2.toFixed(0)} kWh/m²/an (${obj2030.reductionPct} %)`
          : null,
        result.eligible === false
          ? `Non éligible: ${result.message ?? ''}`
          : 'Éligible décret tertiaire',
      ]
        .filter(Boolean)
        .join('\n');

      await apiFetch('/api/leads', {
        json: {
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          message,
          source: 'form',
          segment: 'entreprise',
          intent: 'conformite',
          consentMarketing: false,
        },
      });
      captureEvent('decret_tertiaire_lead_submit', {
        building_type: form.buildingType,
        surface: form.surfaceSqm,
        eligible: result.eligible,
      });
      setDone(true);
    } catch (error) {
      setErr(
        error instanceof ApiError ? error.message : "Impossible d'envoyer la demande. Réessayez.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50/60 p-4">
      <p className="text-[12px] font-bold uppercase tracking-widest text-stone-700">
        Recevoir mon plan d&apos;actions
      </p>
      <p className="mt-1 text-[12.5px] text-stone-600">
        Laissez votre e-mail : on vous recontacte avec le plan OPERAT chiffré (sans engagement).
      </p>
      <form onSubmit={onSubmit} className="mt-3 space-y-2.5">
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            type="text"
            name="firstName"
            autoComplete="given-name"
            placeholder="Prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={loading}
            className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="E-mail professionnel"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
        {err && (
          <p className="text-[12px] font-medium text-red-600" role="alert">
            {err}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="submit"
            variant="brand"
            size="lg"
            disabled={loading}
            className="flex-1 gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Envoi…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Recevoir mon plan OPERAT
              </>
            )}
          </Button>
          <Button asChild variant="secondary" size="lg" className="flex-1 gap-2">
            <a href="/entreprises/diagnostic-cee">Voir aussi les primes CEE →</a>
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ───── Subcomponents ─────────────────────────────────────────────────── */
function KpiTile({
  icon: Icon,
  label,
  value,
  sublabel,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sublabel?: string;
  tone: 'brand' | 'lavender' | 'accent';
}) {
  const colors: Record<typeof tone, { bg: string; text: string; iconBg: string }> = {
    brand: { bg: 'bg-brand-50/60', text: 'text-brand-800', iconBg: 'bg-brand-100 text-brand-700' },
    lavender: {
      bg: 'bg-lavender-50/60',
      text: 'text-lavender-800',
      iconBg: 'bg-lavender-100 text-lavender-700',
    },
    accent: {
      bg: 'bg-accent-50/60',
      text: 'text-accent-800',
      iconBg: 'bg-accent-100 text-accent-700',
    },
  };
  const c = colors[tone];

  return (
    <div className={cn('rounded-xl border border-stone-200 p-3', c.bg)}>
      <div className="flex items-center gap-2">
        <div className={cn('flex h-7 w-7 items-center justify-center rounded-lg', c.iconBg)}>
          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        </div>
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-stone-500">
          {label}
        </p>
      </div>
      <p className={cn('mt-2 font-display text-[16px] font-bold tabular-nums', c.text)}>{value}</p>
      {sublabel && <p className="mt-0.5 text-[10px] text-stone-400">{sublabel}</p>}
    </div>
  );
}

function ObjectiveTile({
  year,
  reductionPct,
  kwhM2,
  highlight,
}: {
  year: number;
  reductionPct: number;
  kwhM2: number;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-all',
        highlight
          ? 'border-lavender-300 bg-lavender-50/60 ring-1 ring-inset ring-lavender-200/60'
          : 'border-stone-200 bg-white',
      )}
    >
      <p
        className={cn(
          'text-[11px] font-bold uppercase tracking-[0.14em]',
          highlight ? 'text-lavender-700' : 'text-stone-500',
        )}
      >
        Échéance {year}
      </p>
      <p className="mt-1.5 font-display text-2xl font-bold tabular-nums text-ink">
        −{reductionPct} %
      </p>
      <p className="mt-0.5 text-[11px] text-stone-500">soit {fmt(kwhM2)} kWh ep/m²/an</p>
    </div>
  );
}
